import * as React from 'react';
import { ArrowUp, Square } from 'lucide-react';
import { Button } from '@/shared/ui/button';
import { cn } from '@/shared/lib/cn';

export type PromptInputProps = React.FormHTMLAttributes<HTMLFormElement>;

export const PromptInput = React.forwardRef<HTMLFormElement, PromptInputProps>(
  ({ className, ...props }, ref) => (
    <form
      ref={ref}
      className={cn(
        'flex items-end gap-2 rounded-xl border border-border/70 bg-[hsl(var(--glass-bg)/0.6)] p-2 shadow-glass-sm backdrop-blur-sm',
        'transition-[box-shadow,border-color] duration-200',
        'focus-within:border-primary/50 focus-within:ring-2 focus-within:ring-ring/40 focus-within:ring-offset-0',
        className,
      )}
      {...props}
    />
  ),
);
PromptInput.displayName = 'PromptInput';

export interface PromptInputTextareaProps extends React.TextareaHTMLAttributes<HTMLTextAreaElement> {
  /** Envia com Enter (Shift+Enter quebra linha) — comportamento padrão de chat. */
  onSubmitShortcut?: () => void;
}

export const PromptInputTextarea = React.forwardRef<HTMLTextAreaElement, PromptInputTextareaProps>(
  ({ className, onSubmitShortcut, onKeyDown, ...props }, ref) => (
    <textarea
      ref={ref}
      rows={2}
      className={cn(
        'min-h-[4.5rem] max-h-32 flex-1 resize-none overflow-y-auto border-0 bg-transparent px-2 py-2 shadow-none',
        'text-[16px] sm:text-sm placeholder:text-muted-foreground/70',
        'outline-none ring-0 focus:outline-none focus-visible:outline-none focus-visible:ring-0 focus-visible:shadow-none',
        'disabled:cursor-not-allowed disabled:opacity-50',
        className,
      )}
      onKeyDown={(e) => {
        onKeyDown?.(e);
        if (e.key === 'Enter' && !e.shiftKey) {
          e.preventDefault();
          onSubmitShortcut?.();
        }
      }}
      {...props}
    />
  ),
);
PromptInputTextarea.displayName = 'PromptInputTextarea';

export interface PromptInputSubmitProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  status?: 'ready' | 'submitted' | 'streaming' | 'error';
}

/** Botão de enviar — vira "parar" (quadrado) enquanto a resposta está streamando. */
export const PromptInputSubmit = React.forwardRef<HTMLButtonElement, PromptInputSubmitProps>(
  ({ status = 'ready', className, ...props }, ref) => {
    const isBusy = status === 'submitted' || status === 'streaming';
    return (
      <Button
        ref={ref}
        type="submit"
        size="icon"
        variant="primary"
        className={cn('shrink-0', className)}
        aria-label={isBusy ? 'Parar' : 'Enviar'}
        {...props}
      >
        {isBusy ? <Square className="size-3.5 fill-current" /> : <ArrowUp className="size-4" />}
      </Button>
    );
  },
);
PromptInputSubmit.displayName = 'PromptInputSubmit';
